import {
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconChevronRight, IconRefresh } from '@tabler/icons';
import { Link, useParams } from 'react-router-dom';
import { DetailRow, StatusBadge, UserAvatar } from '../../components';
import { useUser } from '../../queries/use-users';

function formatDate(value: string | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function UserContainer() {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading, isFetching, isError, error, refetch } = useUser(userId ?? null);

  return (
    <Stack spacing="xl">
      <Breadcrumbs separator={<IconChevronRight size={12} />}>
        <Anchor component={Link} to="/users" color="dimmed" size="sm">
          Users
        </Anchor>
        <Text size="sm">{user?.name ?? user?.email ?? userId}</Text>
      </Breadcrumbs>

      <Box>
        <Group position="apart" align="flex-start" noWrap>
          <Group spacing="md" align="center" noWrap>
            <UserAvatar user={user} size={42} radius="md" />
            <Box>
              <Group spacing="sm" align="center">
                <Title order={2}>{user?.name ?? user?.email ?? 'User'}</Title>
                {user && <StatusBadge status={user.status} />}
              </Group>
              <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
                {userId}
              </Text>
            </Box>
          </Group>
          <Button
            leftIcon={<IconRefresh size={16} />}
            variant="subtle"
            color="bagel"
            size="sm"
            onClick={() => refetch()}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>
      </Box>

      {user ? (
        <Paper withBorder p="lg">
          <Group spacing="xl" align="flex-start" noWrap={false}>
            <DetailRow label="Email" value={user.email} />
            <DetailRow label="Name" value={user.name ?? '—'} />
            <DetailRow
              label="Account"
              value={
                user.accountId ? (
                  <Anchor
                    component={Link}
                    to={`/account/${user.accountId}`}
                    size="sm"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {user.accountId}
                  </Anchor>
                ) : (
                  '—'
                )
              }
            />
            <DetailRow label="Role" value={user.role ?? '—'} />
            <DetailRow label="Status" value={<StatusBadge status={user.status} />} />
            <DetailRow label="Created" value={formatDate(user.createdAt)} />
            <DetailRow
              label="Auth0 Sub"
              value={
                user.sub ? (
                  <Text size="sm" sx={{ fontFamily: 'monospace' }}>
                    {user.sub}
                  </Text>
                ) : (
                  '—'
                )
              }
            />
            <DetailRow
              label="ID"
              value={
                <Text size="sm" sx={{ fontFamily: 'monospace' }}>
                  {user._id}
                </Text>
              }
            />
          </Group>
        </Paper>
      ) : isLoading ? (
        <Center mih={200}>
          <Loader />
        </Center>
      ) : isError ? (
        <Text color="red">Failed to load user: {(error as Error)?.message ?? 'Unknown error'}</Text>
      ) : (
        <Text color="dimmed">User not found.</Text>
      )}
    </Stack>
  );
}
